// src/components/PermissionEditor.js
// Modal component for editing a user's role and page-level permissions
// Called from UserManagementSystem when super_admin clicks "Permissions" on a user

import React, { useState, useEffect } from 'react';
import { PAGES, isSuperAdmin } from '../utils/permissions';

const API = process.env.REACT_APP_API_URL || '';

export default function PermissionEditor({ user, currentUser, onClose, onSaved }) {
  const [role, setRole] = useState(user?.role || 'customer');
  const [permissions, setPermissions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      setRole(user.role || 'customer');
      const perms = Array.isArray(user.permissions) ? user.permissions : [];
      // If super_admin, they have all pages implicitly
      setPermissions(perms.includes('all') ? Object.keys(PAGES) : perms);
    }
  }, [user]);

  const togglePermission = (pageKey) => {
    setPermissions(prev =>
      prev.includes(pageKey) ? prev.filter(p => p !== pageKey) : [...prev, pageKey]
    );
  };

  const selectAll = () => setPermissions(Object.keys(PAGES));
  const selectNone = () => setPermissions([]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('authToken');
      const body = { role };

      // Set permissions based on role
      if (role === 'super_admin') {
        body.permissions = ['all'];
      } else if (role === 'admin') {
        body.permissions = permissions;
      } else {
        body.permissions = [];
      }

      const res = await fetch(`${API}/api/users/${user.id}/permissions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (res.ok && (data.success || data.message)) {
        onSaved && onSaved();
        onClose();
      } else {
        setError(data.error || 'Failed to update permissions');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    }
    setSaving(false);
  };

  if (!user || !isSuperAdmin(currentUser)) return null;

  const isEditingSelf = user.id === currentUser?.id;

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Edit Permissions</h2>
            <p style={{ fontSize: 14, color: '#6B7280', marginTop: 4 }}>{user.first_name || user.firstName} {user.last_name || user.lastName} · {user.email}</p>
          </div>
          <button onClick={onClose} style={closeBtnStyle}>✕</button>
        </div>

        {/* Role selector */}
        <div style={{ marginBottom: 24 }}>
          <label style={labelStyle}>Role</label>
          <div style={{ display: 'flex', gap: 10 }}>
            {['super_admin', 'admin', 'customer'].map(r => (
              <button
                key={r}
                onClick={() => !isEditingSelf && setRole(r)}
                disabled={isEditingSelf}
                style={{
                  flex: 1, padding: '12px 16px', borderRadius: 12, border: `2px solid ${role === r ? '#10B981' : '#E5E7EB'}`,
                  background: role === r ? '#F0FDF4' : '#FFF', cursor: isEditingSelf ? 'not-allowed' : 'pointer',
                  opacity: isEditingSelf ? 0.6 : 1, textAlign: 'center',
                }}
              >
                <div style={{ fontSize: 18, marginBottom: 4 }}>
                  {r === 'super_admin' ? '👑' : r === 'admin' ? '🔑' : '👤'}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: role === r ? '#059669' : '#374151', textTransform: 'capitalize' }}>
                  {r.replace('_', ' ')}
                </div>
                <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                  {r === 'super_admin' ? 'Full access' : r === 'admin' ? 'Selected pages' : 'App only'}
                </div>
              </button>
            ))}
          </div>
          {isEditingSelf && (
            <p style={{ fontSize: 12, color: '#F59E0B', marginTop: 8 }}>You cannot change your own role.</p>
          )}
        </div>

        {/* Page permissions — only show for admin role */}
        {role === 'admin' && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <label style={labelStyle}>Page Access</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={selectAll} style={linkBtnStyle}>Select all</button>
                <span style={{ color: '#D1D5DB' }}>·</span>
                <button onClick={selectNone} style={linkBtnStyle}>Clear all</button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {Object.values(PAGES).map(page => (
                <label
                  key={page.key}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px',
                    borderRadius: 12, border: `1.5px solid ${permissions.includes(page.key) ? '#10B981' : '#E5E7EB'}`,
                    background: permissions.includes(page.key) ? '#F0FDF9' : '#FFF', cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={permissions.includes(page.key)}
                    onChange={() => togglePermission(page.key)}
                    style={{ width: 18, height: 18, accentColor: '#10B981', marginTop: 1, flexShrink: 0 }}
                  />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                      {page.icon} {page.label}
                    </div>
                    <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2, lineHeight: '1.4' }}>
                      {page.description}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Super admin notice */}
        {role === 'super_admin' && (
          <div style={{ background: '#FEF3C7', borderRadius: 12, padding: 14, marginBottom: 24, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <span style={{ fontSize: 16 }}>⚠️</span>
            <p style={{ fontSize: 13, color: '#92400E', lineHeight: '1.5', margin: 0 }}>
              Super admins have full unrestricted access to all pages and can manage other users' permissions.
            </p>
          </div>
        )}

        {/* Customer notice */}
        {role === 'customer' && (
          <div style={{ background: '#F3F4F6', borderRadius: 12, padding: 14, marginBottom: 24, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <span style={{ fontSize: 16 }}>ℹ️</span>
            <p style={{ fontSize: 13, color: '#6B7280', lineHeight: '1.5', margin: 0 }}>
              Customers can only use the mobile app. They have no access to the admin dashboard.
            </p>
          </div>
        )}

        {error && (
          <div style={{ background: '#FEE2E2', color: '#DC2626', borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 14, fontWeight: 500 }}>
            {error}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={cancelBtnStyle}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ ...saveBtnStyle, opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving...' : 'Save Permissions'}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlayStyle = { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const modalStyle = { background: 'white', borderRadius: 20, padding: 32, maxWidth: 640, width: '90%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.25)' };
const closeBtnStyle = { background: '#F3F4F6', border: 'none', width: 32, height: 32, borderRadius: 8, fontSize: 16, cursor: 'pointer', color: '#6B7280', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' };
const linkBtnStyle = { background: 'none', border: 'none', color: '#10B981', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 };
const cancelBtnStyle = { padding: '10px 20px', borderRadius: 10, border: '1px solid #E5E7EB', background: 'white', cursor: 'pointer', fontWeight: 600, fontSize: 14, color: '#374151' };
const saveBtnStyle = { padding: '10px 24px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#10B981,#3B82F6)', color: 'white', cursor: 'pointer', fontWeight: 600, fontSize: 14 };
